import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useRootSelector } from '../../../../../../store/hooks';
import { selectDelegateCountryOptions } from '../../../../store/features/delegate/selectors/selectDelegateCountryOptions';
import { selectDelegateRegionOptions } from '../../../../store/features/delegate/selectors/selectDelegateRegionOptions';
import { PricingFormInput } from '../../../../store/features/delegate/types';
import { PricingFormInputOption } from '../../../../types';
import { PricingFormInputOptionSource } from '../../../../types/enums';
import { DelegateFormProps } from '../../../../pages/ConfirmPurchase/components/DelegateForm';

export interface UseDelegateAdHocInputOptionsProps
  extends Pick<DelegateFormProps, 'delegateUuid'>,
    Pick<PricingFormInput, 'options' | 'optionsSource'> {
  formInputs: PricingFormInput[];
}

export function useDelegateAdHocFormInputOptions(
  props: UseDelegateAdHocInputOptionsProps,
): PricingFormInputOption[] {
  const { options, optionsSource, formInputs, delegateUuid } = props;

  const inputOptions = options || [];
  const isCountriesSource =
    optionsSource === PricingFormInputOptionSource.COUNTRIES;
  const isRegionsSource =
    optionsSource === PricingFormInputOptionSource.REGIONS;

  // Generated country field name
  const countryFieldName = useMemo(() => {
    if (!isRegionsSource) {
      return null;
    }

    // Found first country form input option
    const countryFormInput = formInputs.find(
      (input) => input.optionsSource === PricingFormInputOptionSource.COUNTRIES,
    );

    return countryFormInput ? `adHocAnswers.${countryFormInput.name}` : null;
  }, [isRegionsSource, formInputs]);

  // Selected country field's value itself
  const selectedCountry = useWatch({
    name: countryFieldName || '',
    disabled: !isRegionsSource,
  }) as string;

  const countries = useRootSelector((state) =>
    selectDelegateCountryOptions(state, delegateUuid),
  );
  const regions = useRootSelector((state) =>
    selectDelegateRegionOptions(state, delegateUuid, selectedCountry),
  );

  // Use countries from delegate meta as input options
  if (isCountriesSource && countries.length > 0) {
    return countries;
  }

  // Use regions from delegate meta as input options
  if (isRegionsSource && regions.length > 0) {
    return regions;
  }

  // Use ad hoc set input options as default
  return inputOptions;
}
